import { useRef, type ReactNode, type RefObject } from 'react';
import { useOverlay, DismissButton, FocusScope } from 'react-aria';
import s from './Popover.module.scss';

interface PopoverProps {
  popoverRef?: RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
}

/** Popover based on react-aria — https://react-spectrum.adobe.com/react-aria/useComboBox.html */
function Popover({
  popoverRef: popoverRefProp,
  isOpen,
  onClose,
  children,
}: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const popoverRef = popoverRefProp ?? ref;

  // Handle events that should cause the popup to close,
  // e.g. blur, clicking outside, or pressing the escape key.
  const { overlayProps } = useOverlay(
    {
      isOpen,
      onClose,
      shouldCloseOnBlur: true,
      isDismissable: true,
    },
    popoverRef
  );

  // Add a hidden <DismissButton> component at the end of the popover
  // to allow screen reader users to dismiss the popup easily.
  return (
    <FocusScope restoreFocus>
      <div
        /* oxlint-disable-next-line react/jsx-props-no-spreading */
        {...overlayProps}
        ref={popoverRef}
        className={s.popover}
      >
        <DismissButton onDismiss={onClose} />
        {children}
      </div>
    </FocusScope>
  );
}

export default Popover;
